import { z } from 'zod';
/**
 * Cấu hình S3 do admin nhập runtime — docs/01-tech-stack.md §5.1, ADR-003.
 *
 * ⚠️ Không schema nào ở đây chứa secret key thật. API **không bao giờ** trả nó,
 * kể cả cho `SUPER_ADMIN` (docs/security.md §6). Chỉ có `secretKeyLast4`.
 */
export const StorageProvider = z.enum(['s3', 'r2', 'minio', 'gcs_interop']);
export const ServerSideEncryption = z.enum(['none', 'AES256', 'aws:kms']);
/**
 * Gốc URL cho trình duyệt — khi bucket không đặt được CORS.
 *
 * Trình duyệt nhận presigned URL với origin của bucket được thay bằng giá trị
 * này (giữ nguyên path và query đã ký), còn reverse proxy cùng tên miền với app
 * chuyển tiếp về bucket. Cùng origin thì không còn CORS để mà thiếu.
 *
 * Không nhận query hay fragment: phần đó của presigned URL là chữ ký, ghép thêm
 * vào là hỏng chữ ký.
 */
export const StorageBrowserBaseUrl = z
    .url('URL cho trình duyệt phải là URL hợp lệ')
    .max(255)
    .refine((value) => /^https?:\/\//i.test(value), {
    message: 'URL cho trình duyệt phải bắt đầu bằng http:// hoặc https://',
})
    .refine((value) => !/[?#]/.test(value), {
    message: 'URL cho trình duyệt không được có query (?…) hay fragment (#…)',
});
export const StorageBrowserBaseUrlInput = z.object({
    /** `null` = bỏ proxy, trình duyệt gọi thẳng bucket. */
    browserBaseUrl: StorageBrowserBaseUrl.nullable(),
});
export const StorageConfigInput = z
    .object({
    provider: StorageProvider,
    /** Bỏ trống = AWS S3 chuẩn. Bắt buộc cho R2/MinIO. */
    endpoint: z.url('Endpoint phải là URL hợp lệ').max(255).optional(),
    region: z.string().min(1, 'Region là bắt buộc').max(40),
    bucket: z.string().min(1, 'Tên bucket là bắt buộc').max(255),
    accessKeyId: z.string().min(1, 'Access key ID là bắt buộc').max(255),
    secretAccessKey: z.string().min(1, 'Secret access key là bắt buộc').max(255),
    /** MinIO và một số triển khai R2 bắt buộc `true`. */
    forcePathStyle: z.boolean().default(false),
    sse: ServerSideEncryption.default('AES256'),
    browserBaseUrl: StorageBrowserBaseUrl.optional(),
})
    .refine((value) => value.provider === 's3' || value.endpoint !== undefined, {
    // AWS S3 tự suy ra endpoint từ region; mọi provider khác thì không.
    // Thiếu endpoint ở đó nghĩa là SDK lặng lẽ gọi vào aws.amazon.com.
    message: 'Provider này bắt buộc phải có endpoint',
    path: ['endpoint'],
});
export const StorageConfigView = z.object({
    id: z.uuid(),
    provider: StorageProvider,
    endpoint: z.string().nullable(),
    region: z.string(),
    bucket: z.string(),
    accessKeyId: z.string(),
    /** Bốn ký tự cuối của secret — đủ để admin nhận ra mình đang nhìn key nào. */
    secretKeyLast4: z.string().length(4),
    forcePathStyle: z.boolean(),
    sse: ServerSideEncryption,
    /** `null` = trình duyệt gọi thẳng bucket (cần CORS của bucket). */
    browserBaseUrl: z.string().nullable(),
    isActive: z.boolean(),
    /** Lần cuối test connection thành công. `null` = chưa bao giờ pass. */
    verifiedAt: z.iso.datetime().nullable(),
    createdAt: z.iso.datetime(),
});
/**
 * Kết quả "Test connection".
 *
 * Ba bước riêng biệt vì `ListBuckets` pass **không** có nghĩa là ghi được:
 * rất nhiều IAM policy cho list nhưng chặn `PutObject`. Báo rõ bước nào hỏng
 * để admin biết phải sửa policy nào (ADR-003, bẫy số 3).
 */
export const StorageTestStep = z.enum(['put', 'get', 'delete']);
export const StorageTestResult = z.object({
    ok: z.boolean(),
    steps: z.array(z.object({
        step: StorageTestStep,
        ok: z.boolean(),
        /** Thông điệp lỗi đã lọc, không chứa credential. */
        error: z.string().optional(),
    })),
});
/** Cho banner cảnh báo và việc disable nút upload — docs/01-tech-stack.md §5.5. */
export const StorageStatus = z.object({
    configured: z.boolean(),
    activeConfigId: z.uuid().nullable(),
    bucket: z.string().nullable(),
    provider: StorageProvider.nullable(),
});
/**
 * Dung lượng theo tầng — màn hình admin của APM-203.
 *
 * Tách nóng/nguội chứ không gộp một con số: hai tầng có chi phí và ràng buộc
 * hoàn toàn khác nhau. Tầng nóng nằm trong MySQL và là thứ phình lên làm chậm
 * backup; tầng nguội nằm trên S3 và chỉ tốn tiền lưu trữ.
 */
export const StorageUsage = z.object({
    hot: z.object({
        /** Kích thước bảng `event_log` trong MySQL, gồm cả index. */
        eventLogBytes: z.number().int().nonnegative(),
        eventCount: z.number().int().nonnegative(),
        /** Partition đang có, cũ nhất trước. */
        partitions: z.array(z.string()),
        snapshotBytes: z.number().int().nonnegative(),
        snapshotCount: z.number().int().nonnegative(),
    }),
    cold: z.object({
        /** Tổng kích thước tệp `.jsonl.gz` đã đẩy lên S3. */
        archivedEventBytes: z.number().int().nonnegative(),
        archivedEventCount: z.number().int().nonnegative(),
        archivedPeriods: z.array(z.string()),
        snapshotCount: z.number().int().nonnegative(),
    }),
    files: z.object({
        committedBytes: z.number().int().nonnegative(),
        committedCount: z.number().int().nonnegative(),
        /** Upload dở dang chờ job dọn — con số này lớn dần là dấu hiệu job chết. */
        pendingCount: z.number().int().nonnegative(),
    }),
});
//# sourceMappingURL=storage.js.map